import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Sparkles, ThumbsUp, Trash2, X } from "lucide-react";
import { api, fileUrl } from "../../api/client";
import { Badge } from "../../components/Badge";
import type { BadgeTone } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { useAuth } from "../auth/useAuth";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { CHANGELOG_SEEN_EVENT } from "./unread";

// 更新紀錄與留言板(業主 2026-09-28):
//   > 側邊攔 在其他追加 更新紀錄 / 許願池功能 / 請求更新功能 / 系統更新紀錄
//
// 兩個分頁:系統更新紀錄(內容是 repo 裡的 docs/changelog.md,跟著部署)
// 與留言板(許願 + 要修合成一個,業主選的)。全員都看得到。
//
// **打開這一頁就算看過了** —— 側邊欄的紅點歸零。這次新的那幾項標一個「新」,
// 讓人知道紅點數的是哪幾項。

interface ChangeItem {
  seq: number;
  kind: "新增" | "修正" | "調整";
  title: string;
  detail: string;
}

interface ChangeDay {
  date: string;
  items: ChangeItem[];
}

interface Changelog {
  seen: number;
  days: ChangeDay[];
}

export interface BoardPost {
  id: number;
  kind: "WISH" | "FIX";
  body: string;
  image_url: string | null;
  status: "NEW" | "DOING" | "DONE" | "WONT";
  author: string;
  created_at: string;
  votes: number;
  voted: boolean;
  can_delete: boolean;
}

const KIND_TONE: Record<ChangeItem["kind"], BadgeTone> = { 新增: "ok", 修正: "danger", 調整: "accent" };
const POST_KIND = { WISH: "許願", FIX: "要修" } as const;
const STATUS = { NEW: "新提出", DOING: "處理中", DONE: "已完成", WONT: "不做" } as const;
const STATUS_TONE: Record<BoardPost["status"], BadgeTone> = {
  NEW: "neutral",
  DOING: "warn",
  DONE: "ok",
  WONT: "neutral",
};
const STATUS_OPTIONS = (Object.keys(STATUS) as BoardPost["status"][]).map((value) => ({
  value,
  label: STATUS[value],
}));

type Tab = "changes" | "board";

export function UpdatesPage() {
  const [tab, setTab] = useState<Tab>("changes");
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <h2 className="m-0 flex items-center gap-2 text-xl font-semibold text-[var(--text)]">
        <Sparkles aria-hidden="true" size={20} />
        更新紀錄
      </h2>
      <div role="tablist" aria-label="更新紀錄分頁" className="flex gap-1 border-b border-[var(--border)]">
        {(
          [
            ["changes", "系統更新紀錄"],
            ["board", "留言板"],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            type="button"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium " +
              (tab === key
                ? "border-[var(--accent)] text-[var(--text)]"
                : "border-transparent text-[var(--text-2)] hover:text-[var(--text)]")
            }
          >
            {label}
          </button>
        ))}
      </div>
      {/* 更新紀錄一直掛著:切到留言板再切回來,「新」的標記不該因為重抓而消失。 */}
      <div hidden={tab !== "changes"}>
        <Changes />
      </div>
      {tab === "board" ? <Board /> : null}
    </div>
  );
}

function Changes() {
  const log = useQuery({ queryKey: ["changelog"], queryFn: () => api.get<Changelog>("/changelog") });
  // 看過 = 讀到清單之後。先讀再標,``seen`` 才是「打開之前看到哪裡」,「新」才標得出來。
  const marked = useRef(false);
  useEffect(() => {
    if (!log.data || marked.current) return;
    marked.current = true;
    void api
      .post("/changelog/seen")
      .then(() => window.dispatchEvent(new Event(CHANGELOG_SEEN_EVENT)))
      .catch(() => {});
  }, [log.data]);

  if (log.isLoading) return <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>;
  const days = log.data?.days ?? [];
  const seen = log.data?.seen ?? 0;
  if (days.length === 0) return <p className="m-0 text-sm text-[var(--text-3)]">還沒有更新紀錄。</p>;

  return (
    <div className="flex flex-col gap-5">
      {days.map((day) => (
        <section key={day.date} className="flex flex-col gap-2">
          <h3 className="m-0 text-sm font-semibold text-[var(--text-2)]">{day.date.replace(/-/g, "/")}</h3>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {day.items.map((item) => (
              <li
                key={item.seq}
                className="flex items-start gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-3"
              >
                <Badge tone={KIND_TONE[item.kind]}>{item.kind}</Badge>
                <div className="min-w-0 flex-1">
                  <p className="m-0 font-medium text-[var(--text)]">
                    {item.title}
                    {item.seq > seen ? (
                      <span className="ml-2 rounded-full bg-[var(--danger)] px-1.5 text-xs font-semibold text-[var(--on-danger)]">
                        新
                      </span>
                    ) : null}
                  </p>
                  {item.detail ? <p className="m-0 mt-0.5 text-sm text-[var(--text-2)]">{item.detail}</p> : null}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function Board() {
  const toast = useToast();
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<BoardPost["kind"]>("WISH");
  const [body, setBody] = useState("");
  const [image, setImage] = useState<{ file_id: number; url: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const posts = useQuery({ queryKey: ["board"], queryFn: () => api.get<BoardPost[]>("/board") });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["board"] });
  const fail = (fallback: string) => (err: unknown) =>
    toast.push(err instanceof Error ? err.message : fallback, "error");

  const create = useMutation({
    mutationFn: () => api.post("/board", { kind, body: body.trim(), image_file_id: image?.file_id ?? null }),
    onSuccess: () => {
      setBody("");
      setImage(null);
      toast.push("送出了,謝謝", "success");
      refresh();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "送不出去"),
  });
  const vote = useMutation({ mutationFn: (id: number) => api.post(`/board/${id}/vote`), onSuccess: refresh, onError: fail("投不了") });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => api.patch(`/board/${id}/status`, { status }),
    onSuccess: refresh,
    onError: fail("改不了狀態"),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/board/${id}`),
    onSuccess: () => {
      toast.push("已丟進回收桶,七天內救得回來", "success");
      refresh();
    },
    onError: fail("刪不了"),
  });

  const pickImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      setImage(await api.upload<{ file_id: number; url: string }>("/board/images", form));
    } catch (err) {
      setError(err instanceof Error ? err.message : "圖片傳不上去");
    } finally {
      setUploading(false);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (body.trim() === "") {
      setError("請寫下內容");
      return;
    }
    setError(null);
    create.mutate();
  };

  const list = Array.isArray(posts.data) ? posts.data : [];

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-sm text-[var(--text-2)]">
        想要系統多一個功能就「許願」,哪裡壞了、不好用就「要修」。看到別人寫的你也想要,按 +1。
      </p>
      <form
        onSubmit={submit}
        className="flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-3"
      >
        <fieldset className="m-0 flex gap-4 border-0 p-0">
          <legend className="sr-only">種類</legend>
          {(["WISH", "FIX"] as const).map((value) => (
            <label key={value} className="inline-flex items-center gap-1.5 text-sm text-[var(--text)]">
              <input
                type="radio"
                name="board-kind"
                value={value}
                checked={kind === value}
                onChange={() => setKind(value)}
              />
              {POST_KIND[value]}
            </label>
          ))}
        </fieldset>
        <label htmlFor="board-body" className="text-sm font-medium text-[var(--text-2)]">
          內容
        </label>
        <textarea
          id="board-body"
          rows={3}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          className="w-full rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] p-2 text-sm text-[var(--text)]"
        />
        {image ? (
          <div className="flex items-start gap-2">
            <img src={fileUrl(image.url)} alt="要附上的圖片" className="max-h-40 rounded-[var(--radius)]" />
            <Button
              size="sm"
              variant="ghost"
              aria-label="拿掉這張圖"
              icon={<X aria-hidden="true" size={14} />}
              onClick={() => setImage(null)}
            />
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--radius)] border border-[var(--border-strong)] px-2.5 py-1.5 text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)]">
            <ImagePlus aria-hidden="true" size={16} />
            {uploading ? "上傳中…" : "附一張截圖"}
            <input
              type="file"
              accept="image/*"
              aria-label="附一張截圖"
              className="sr-only"
              disabled={uploading}
              onChange={(event) => void pickImage(event)}
            />
          </label>
          <Button type="submit" variant="primary" loading={create.isPending} disabled={uploading}>
            送出
          </Button>
        </div>
        {error ? (
          <p role="alert" className="m-0 text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
      </form>

      {posts.isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {!posts.isLoading && list.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-3)]">還沒有人留言。</p>
      ) : null}
      <ul aria-label="留言" className="m-0 flex list-none flex-col gap-3 p-0">
        {list.map((post) => (
          <li
            key={post.id}
            className="flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={post.kind === "FIX" ? "danger" : "accent"}>{POST_KIND[post.kind]}</Badge>
              {isOwner ? (
                <div className="w-28">
                  <Select
                    aria-label={`狀態:${post.body}`}
                    options={STATUS_OPTIONS}
                    value={post.status}
                    onChange={(event) => setStatus.mutate({ id: post.id, status: event.target.value })}
                  />
                </div>
              ) : (
                <Badge tone={STATUS_TONE[post.status]}>{STATUS[post.status]}</Badge>
              )}
            </div>
            <p className="m-0 whitespace-pre-wrap text-[var(--text)]">{post.body}</p>
            {post.image_url ? (
              <img src={fileUrl(post.image_url)} alt="留言附的圖" className="max-h-80 self-start rounded-[var(--radius)]" />
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--text-3)]">
              <span>
                {post.author}・{formatTaipeiDateTime(post.created_at)}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant={post.voted ? "primary" : "ghost"}
                  aria-label={`+1 ${post.body.slice(0, 20)}`}
                  aria-pressed={post.voted}
                  icon={<ThumbsUp aria-hidden="true" size={14} />}
                  onClick={() => vote.mutate(post.id)}
                >
                  {post.votes}
                </Button>
                {post.can_delete ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 aria-hidden="true" size={14} />}
                    aria-label={`刪除這則留言(${post.body.slice(0, 12)})`}
                    onClick={() => {
                      if (window.confirm("刪除這則留言嗎?它會進回收桶,七天內救得回來。")) {
                        remove.mutate(post.id);
                      }
                    }}
                  >
                    刪除
                  </Button>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
